/**
 * KanbanBoard — tryb „Kanban" dla ConfigurableTable.
 *
 * Grupuje rekordy po kolumnie wskazanej w `kanbanConfig.groupByFieldId`
 * (fallback: pierwsza kolumna typu select z `headerFilter.options`). Każda opcja
 * = osobna kolumna, rekordy bez wartości trafiają do „Nieprzypisane". Wygląd
 * kafelka (które pola pokazać) sterowany `kanbanConfig.cardFields`; styl tablicy
 * (szerokość/odstępy/padding/tło/obramowanie kolumn + nagłówek) sterowany
 * `kanbanConfig.boardStyle` (brak = `defaultKanbanBoardStyle`).
 *
 * Drag & drop — aktywny gdy strona dostarczy `onRowUpdate`. Przeciągnięcie
 * kafelka między kolumnami woła `onRowUpdate(rowId, groupByFieldId, newValue)`
 * (newValue = wartość kolumny docelowej, `null` dla „Nieprzypisane"), z
 * optymistycznym przeniesieniem i rollbackiem przy błędzie. Bez `onRowUpdate`
 * tablica pozostaje read-only (kafelki klikalne, bez przeciągania); tak samo, gdy `canMoveField`
 * odmówi dla pola grupującego (brak prawa edycji, pole nie do zmiany przeciągnięciem).
 */

import type React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type ColumnConfig,
  defaultKanbanBoardStyle,
  type KanbanBoardStyle,
  type KanbanColumnRadius,
  type KanbanConfig,
} from '@saas/shared';

import type { LayoutElement } from '@/lib/layout-tree';

import { hasCardLayout, renderKanbanCardLayout } from './kanban-card-layout';
import type { ColumnDefinition, RowActionsContext } from './types';

/** Kontekst akcji w stopce kafelka — stały obiekt, żeby `renderActions` nie dostawał nowego co render. */
const KANBAN_ACTIONS_CONTEXT: RowActionsContext = { placement: 'kanban' };

interface KanbanColumnData<T> {
  id: string;
  label: string;
  color: string;
  rows: T[];
}

interface KanbanBoardProps<T> {
  /** Widoczne kolumny w kolejności (pierwsza = tytuł kafelka). */
  columns: ColumnDefinition<T>[];
  /** Dane do wyświetlenia (już przetworzone przez ConfigurableTable). */
  data: T[];
  /** Identyfikator wiersza. */
  getRowId: (row: T) => string;
  /** Pobranie wartości komórki dla danej kolumny. */
  getCellValue: (row: T, column: ColumnDefinition<T>) => unknown;
  /** Nadpisana etykieta kolumny (customLabel). */
  getColumnConfig: (columnId: string) => ColumnConfig | undefined;
  /** Konfiguracja Kanban z layoutu (pole grupujące + pola kafelka + styl). */
  kanbanConfig?: KanbanConfig;
  /** Klik w kafelek. */
  onRowClick?: (row: T) => void;
  /**
   * Aktualizacja pola grupującego po przeciągnięciu kafelka. Gdy podany —
   * włącza drag & drop. Wywoływany z `(rowId, groupByFieldId, newValue)`.
   */
  onRowUpdate?: (rowId: string, fieldId: string, newValue: string | null) => Promise<void>;
  /**
   * Czy przy grupowaniu po danym polu wolno przeciągać kafelki. `false` = tablica tylko do
   * podglądu mimo `onRowUpdate` (np. brak prawa edycji, pole nie do zmiany przeciągnięciem).
   * Pominięte = wolno.
   */
  canMoveField?: (fieldId: string) => boolean;
  /** Akcje rekordu (renderowane w stopce kafelka); drugi argument = `{ placement: 'kanban' }`. */
  renderActions?: (row: T, context: RowActionsContext) => React.ReactNode;
  /** Prefiks data-testid. */
  testIdPrefix?: string;
}

const UNASSIGNED = '__unassigned__';

const RADIUS_PX: Record<KanbanColumnRadius, number> = {
  none: 0,
  small: 6,
  medium: 10,
  large: 16,
};

const FONT_WEIGHT: Record<KanbanBoardStyle['header']['fontWeight'], number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// ─── Renderowany kafelek (dane już rozwiązane do węzłów React) ──────────────
interface RenderedCard {
  rowId: string;
  titleNode: React.ReactNode;
  details: Array<{ id: string; label: string; node: React.ReactNode }>;
  actions: React.ReactNode;
  /** Gdy ustawiony `kanbanConfig.cardLayout` — bogaty układ zamiast tytuł+detale. */
  layoutNode?: React.ReactNode;
}

interface CardViewProps {
  card: RenderedCard;
  cardPadding: number;
  clickable: boolean;
  onClick?: () => void;
  testId?: string;
  /** Refs/atrybuty/style z `useSortable` — gdy drag aktywny. */
  dragProps?: {
    setNodeRef: (node: HTMLElement | null) => void;
    style: React.CSSProperties;
    attributes: Record<string, unknown>;
    listeners: Record<string, unknown> | undefined;
    isDragging: boolean;
  };
}

function CardBody({ card, cardPadding, clickable, onClick, testId, dragProps }: CardViewProps) {
  return (
    <div
      ref={dragProps?.setNodeRef}
      onClick={onClick}
      data-testid={testId}
      {...(dragProps?.attributes ?? {})}
      {...(dragProps?.listeners ?? {})}
      className={`rounded-lg border border-gray-200 bg-white transition-shadow dark:border-gray-700 dark:bg-gray-800 ${
        clickable ? 'cursor-pointer hover:shadow-sm' : ''
      } ${dragProps ? 'cursor-grab active:cursor-grabbing' : ''} ${
        dragProps?.isDragging ? 'opacity-40' : ''
      }`}
      style={{ padding: cardPadding, ...(dragProps?.style ?? {}) }}
    >
      {card.layoutNode ? (
        <div className="space-y-1">{card.layoutNode}</div>
      ) : (
        <>
          <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
            {card.titleNode}
          </div>
          <div className="mt-1 space-y-0.5">
            {card.details.map((d) => (
              <div key={d.id} className="flex items-baseline gap-1 text-xs">
                <span className="shrink-0 text-gray-400 dark:text-gray-500">{d.label}:</span>
                <span className="min-w-0 truncate text-gray-600 dark:text-gray-300">{d.node}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {card.actions && (
        <div className="mt-1.5 flex justify-end" onClick={(e) => e.stopPropagation()}>
          {card.actions}
        </div>
      )}
    </div>
  );
}

/** Kafelek z obsługą przeciągania (useSortable). */
function SortableCard(props: Omit<CardViewProps, 'dragProps'>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.card.rowId,
  });
  return (
    <CardBody
      {...props}
      dragProps={{
        setNodeRef,
        style: { transform: CSS.Translate.toString(transform), transition },
        attributes: attributes as unknown as Record<string, unknown>,
        listeners: listeners as Record<string, unknown> | undefined,
        isDragging,
      }}
    />
  );
}

/** Kolumna jako cel upuszczenia (useDroppable). */
function DroppableColumn({
  columnId,
  children,
  className,
  style,
  testId,
}: {
  columnId: string;
  children: React.ReactNode;
  className: string;
  style: React.CSSProperties;
  testId?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: columnId });
  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'ring-2 ring-blue-400 ring-inset' : ''}`}
      style={style}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export function KanbanBoard<T>({
  columns,
  data,
  getRowId,
  getCellValue,
  getColumnConfig,
  kanbanConfig,
  onRowClick,
  onRowUpdate,
  canMoveField,
  renderActions,
  testIdPrefix,
}: KanbanBoardProps<T>) {
  const { t } = useTranslation();

  const style = kanbanConfig?.boardStyle ?? defaultKanbanBoardStyle;
  // Optymistyczne przeniesienia (rowId → nowa wartość pola grupującego / null).
  // Mają pierwszeństwo nad faktyczną wartością w danych dopóki refetch nie
  // dogoni; przy błędzie zapisu wpis jest usuwany (rollback).
  const [optimisticMoves, setOptimisticMoves] = useState<Record<string, string | null>>({});
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Pole grupujące — z konfiguracji (groupByFieldId) z fallbackiem na pierwszą
  // kolumnę select z listą opcji (zachowanie sprzed konfigurowalnego Kanbana).
  const groupColumn = useMemo(() => {
    const configured = kanbanConfig?.groupByFieldId;
    if (configured) {
      const byId = columns.find((c) => c.id === configured);
      if (byId) return byId;
    }
    // `kanbanFallback: false` — opcje dołożone wyłącznie dla wiersza filtrów (patrz
    // `ColumnHeaderFilterConfig.kanbanFallback`) nie zmieniają domyślnego pola grupującego.
    return (
      columns.find(
        (c) =>
          c.headerFilter?.category === 'select' &&
          (c.headerFilter.options?.length ?? 0) > 0 &&
          c.headerFilter.kanbanFallback !== false,
      ) ?? null
    );
  }, [columns, kanbanConfig?.groupByFieldId]);

  // Drag & drop: strona dała zapis ORAZ pozwala zmieniać pole, po którym tablica grupuje.
  const dndEnabled =
    Boolean(onRowUpdate) &&
    groupColumn !== null &&
    (canMoveField ? canMoveField(groupColumn.id) : true);

  const boardColumns = useMemo<KanbanColumnData<T>[]>(() => {
    if (!groupColumn) return [];
    const options = groupColumn.headerFilter?.options ?? [];
    const transformLabel = groupColumn.headerFilter?.transformLabel;

    // Metadane kolumn ze statycznych `options` (etykieta + kolor + kolejność).
    // Gdy `options` puste (np. select z runtime distinct values — status
    // kontrahenta), kolumny wyprowadzamy z faktycznych wartości w danych.
    //
    // Wyjątek: opcje dołożone WYŁĄCZNIE dla wiersza filtrów (kolumna z `kanbanFallback: false`,
    // np. „Opiekun" — lista użytkowników, do kilkuset pozycji) służą tu tylko za etykiety i kolory.
    // Tory powstają wtedy wyłącznie z wartości obecnych w danych (jak przed dołożeniem opcji) —
    // inaczej jawne grupowanie po takiej kolumnie rysowałoby setki pustych torów. Kolumna
    // `record-select` BEZ flagi (np. „Firma") zachowuje tory ze wszystkich opcji, jak dotąd.
    const optionsOnlyForMeta = groupColumn.headerFilter?.kanbanFallback === false;
    const optionMeta = new Map<string, { label: string; color: string }>();
    const order: string[] = [];
    for (const opt of options) {
      optionMeta.set(opt.value, { label: opt.label ?? opt.value, color: opt.color ?? '#6B7280' });
      if (!optionsOnlyForMeta) order.push(opt.value);
    }

    const byValue = new Map<string, T[]>();
    const unassigned: T[] = [];

    for (const row of data) {
      const rowId = getRowId(row);
      // Wartość efektywna: optymistyczne przeniesienie ma pierwszeństwo.
      let key: string | null;
      if (Object.prototype.hasOwnProperty.call(optimisticMoves, rowId)) {
        key = optimisticMoves[rowId] ?? null;
      } else {
        const raw = getCellValue(row, groupColumn);
        key = raw == null || raw === '' ? null : String(raw);
      }
      if (key == null) {
        unassigned.push(row);
        continue;
      }
      if (!byValue.has(key)) {
        byValue.set(key, []);
        // Wartość obecna w danych, ale spoza statycznych `options` (albo — gdy opcje służą tylko
        // za etykiety — każda wartość z danych, w kolejności pojawienia się).
        if (optionsOnlyForMeta || !optionMeta.has(key)) order.push(key);
      }
      byValue.get(key)!.push(row);
    }

    const cols: KanbanColumnData<T>[] = [];
    if (unassigned.length > 0) {
      cols.push({
        id: UNASSIGNED,
        label: t('tenant.listBuilder.viewMode.kanban_meta.unassigned'),
        color: '#9CA3AF',
        rows: unassigned,
      });
    }
    for (const value of order) {
      const meta = optionMeta.get(value);
      const label = meta?.label ?? transformLabel?.(value) ?? value;
      cols.push({
        id: value,
        label,
        color: meta?.color ?? '#6B7280',
        rows: byValue.get(value) ?? [],
      });
    }
    return cols;
  }, [groupColumn, data, getRowId, getCellValue, optimisticMoves, t]);

  const [titleColumn] = columns;

  // Kolumny szczegółów na kafelku: jeśli `cardFields` ustawione → tylko te
  // (w zadanej kolejności), inaczej pierwsze 4 kolumny po tytułowej. Z obu
  // wykluczamy kolumnę tytułową (jest nagłówkiem kafelka) i grupującą.
  const detailColumns = useMemo<ColumnDefinition<T>[]>(() => {
    const excluded = new Set<string>(
      [titleColumn?.id, groupColumn?.id].filter(Boolean) as string[],
    );
    const cardFields = kanbanConfig?.cardFields ?? [];
    if (cardFields.length > 0) {
      return cardFields
        .filter((id) => !excluded.has(id))
        .map((id) => columns.find((c) => c.id === id))
        .filter((c): c is ColumnDefinition<T> => Boolean(c));
    }
    return columns.filter((c) => !excluded.has(c.id)).slice(0, 4);
  }, [columns, titleColumn?.id, groupColumn?.id, kanbanConfig?.cardFields]);

  // Mapa rowId → columnId (do rozwiązania celu gdy upuszczamy NAD kafelkiem).
  const recordToColumn = useMemo(() => {
    const map = new Map<string, string>();
    for (const col of boardColumns) {
      for (const row of col.rows) {
        map.set(getRowId(row), col.id);
      }
    }
    return map;
  }, [boardColumns, getRowId]);

  // Rozwiązanie wiersza po id (do DragOverlay + render kafelka).
  const rowsById = useMemo(() => {
    const map = new Map<string, T>();
    for (const row of data) map.set(getRowId(row), row);
    return map;
  }, [data, getRowId]);

  const cardLayout = (kanbanConfig?.cardLayout as LayoutElement[] | null | undefined) ?? null;

  const renderCard = useMemo(
    () =>
      (row: T, rowIndex: number): RenderedCard => {
        const rowId = getRowId(row);
        const titleValue = titleColumn ? getCellValue(row, titleColumn) : null;
        const titleNode = titleColumn?.renderCell
          ? titleColumn.renderCell(titleValue, row, rowIndex)
          : String(titleValue ?? '—');
        const details = detailColumns
          .map((column) => {
            const value = getCellValue(row, column);
            if (value == null || value === '') return null;
            const config = getColumnConfig(column.id);
            const label = config?.customLabel || column.header;
            const node = column.renderCell
              ? column.renderCell(value, row, rowIndex)
              : (String(value) as React.ReactNode);
            return { id: column.id, label, node };
          })
          .filter((d): d is { id: string; label: string; node: React.ReactNode } => Boolean(d));

        // Bogaty układ z designera: pole → komórka odpowiedniej kolumny.
        let layoutNode: React.ReactNode;
        if (hasCardLayout(cardLayout)) {
          const renderField = (fieldId: string): React.ReactNode => {
            const column = columns.find((c) => c.id === fieldId);
            if (!column) return null;
            const value = getCellValue(row, column);
            if (value == null || value === '') return null;
            return column.renderCell
              ? column.renderCell(value, row, rowIndex)
              : (String(value) as React.ReactNode);
          };
          layoutNode = renderKanbanCardLayout(cardLayout, renderField, 'preview');
        }

        return {
          rowId,
          titleNode,
          details,
          actions: renderActions ? renderActions(row, KANBAN_ACTIONS_CONTEXT) : null,
          layoutNode,
        };
      },
    [
      titleColumn,
      detailColumns,
      columns,
      cardLayout,
      getCellValue,
      getColumnConfig,
      getRowId,
      renderActions,
    ],
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  async function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || !onRowUpdate || !groupColumn) return;

    const draggedId = String(active.id);
    const overId = String(over.id);

    // `over` może być kolumną (drop na pustym obszarze) albo kafelkiem.
    const targetColumnId = boardColumns.some((c) => c.id === overId)
      ? overId
      : (recordToColumn.get(overId) ?? null);
    if (targetColumnId == null) return;

    const sourceColumnId = recordToColumn.get(draggedId) ?? null;
    if (sourceColumnId === targetColumnId) return;

    const newValue = targetColumnId === UNASSIGNED ? null : targetColumnId;
    setOptimisticMoves((prev) => ({ ...prev, [draggedId]: newValue }));
    try {
      await onRowUpdate(draggedId, groupColumn.id, newValue);
    } catch {
      // Rollback — usuń optymistyczne przeniesienie.
      setOptimisticMoves((prev) => {
        const next = { ...prev };
        delete next[draggedId];
        return next;
      });
    }
  }

  if (!groupColumn) {
    return (
      <div
        className="flex flex-col items-center justify-center px-4 py-16 text-center"
        data-testid={testIdPrefix ? `${testIdPrefix}-kanban-empty` : undefined}
      >
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {t('tenant.listBuilder.viewMode.kanban_meta.notConfigured')}
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('tenant.listBuilder.viewMode.kanban_meta.notConfiguredHint')}
        </p>
      </div>
    );
  }

  const radiusPx = RADIUS_PX[style.columnRadius];

  const columnHeader = (col: KanbanColumnData<T>) => (
    <div
      className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700"
      style={{
        padding: style.header.padding,
        justifyContent:
          style.header.align === 'center'
            ? 'center'
            : style.header.align === 'right'
              ? 'flex-end'
              : 'flex-start',
      }}
    >
      {style.header.colorIndicator === 'dot' && (
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: col.color }}
        />
      )}
      {style.header.colorIndicator === 'bar' && (
        <span className="h-4 w-1 shrink-0 rounded-sm" style={{ backgroundColor: col.color }} />
      )}
      <span
        className="truncate text-gray-800 dark:text-gray-100"
        style={{
          fontSize: style.header.fontSize,
          fontWeight: FONT_WEIGHT[style.header.fontWeight],
        }}
      >
        {col.label}
      </span>
      {style.header.counterStyle === 'badge' ? (
        <span className="ml-1 shrink-0 rounded-full bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          {col.rows.length}
        </span>
      ) : (
        <span className="ml-1 shrink-0 text-xs text-gray-400 dark:text-gray-500">
          {col.rows.length}
        </span>
      )}
    </div>
  );

  const emptyColumnPlaceholder = (
    <div className="flex items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-6 text-xs text-gray-400 dark:border-gray-700">
      {t('tenant.listBuilder.viewMode.kanban_meta.emptyColumn')}
    </div>
  );

  const columnClassName = `flex shrink-0 flex-col ${
    style.showColumnBorder ? 'border border-gray-200 dark:border-gray-700' : ''
  } ${style.columnBgColor ? '' : 'bg-gray-50 dark:bg-gray-900'}`;

  const columnStyle: React.CSSProperties = {
    width: style.columnWidth,
    borderRadius: radiusPx,
    backgroundColor: style.columnBgColor ?? undefined,
  };

  const boardInner = boardColumns.map((col) => {
    const cards = col.rows.map((row, rowIndex) => {
      const card = renderCard(row, rowIndex);
      const testId = testIdPrefix ? `${testIdPrefix}-kanban-card-${card.rowId}` : undefined;
      const commonProps = {
        card,
        cardPadding: style.cardPadding,
        clickable: Boolean(onRowClick),
        onClick: onRowClick ? () => onRowClick(row) : undefined,
        testId,
      };
      return dndEnabled ? (
        <SortableCard key={card.rowId} {...commonProps} />
      ) : (
        <CardBody key={card.rowId} {...commonProps} />
      );
    });

    const body = (
      <>
        {columnHeader(col)}
        <div className="flex flex-col p-2" style={{ gap: style.cardGap }}>
          {cards}
          {col.rows.length === 0 && emptyColumnPlaceholder}
        </div>
      </>
    );

    const colTestId = testIdPrefix ? `${testIdPrefix}-kanban-col-${col.id}` : undefined;

    if (dndEnabled) {
      return (
        <DroppableColumn
          key={col.id}
          columnId={col.id}
          className={columnClassName}
          style={columnStyle}
          testId={colTestId}
        >
          <SortableContext
            items={col.rows.map((r) => getRowId(r))}
            strategy={verticalListSortingStrategy}
          >
            {body}
          </SortableContext>
        </DroppableColumn>
      );
    }

    return (
      <div key={col.id} className={columnClassName} style={columnStyle} data-testid={colTestId}>
        {body}
      </div>
    );
  });

  const board = (
    <div
      className="flex gap-3 overflow-x-auto p-4"
      style={{ minHeight: '50vh' }}
      data-testid={testIdPrefix ? `${testIdPrefix}-kanban` : undefined}
    >
      {boardInner}
    </div>
  );

  if (!dndEnabled) return board;

  const activeRow = activeId ? rowsById.get(activeId) : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {board}
      <DragOverlay>
        {activeRow ? (
          <CardBody
            card={renderCard(activeRow, 0)}
            cardPadding={style.cardPadding}
            clickable={false}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
